import { useState } from "react";
import CopyCode from "../CopyCode/CopyCode";
import Icon from "../Icon";
import "./Carousel.css";

const CarouselComponent = () => {
	const [activeSlide, setActiveSlide] = useState({
		carousel1: 0,
		carousel2: 0,
		carousel3: 0,
		carousel4: 0,
		carousel5: 0,
	});

	const slides = [
		{ id: 1, color: "from-amber-500 to-blue-500", icon: "palette" },
		{ id: 2, color: "from-amber-500 to-amber-500", icon: "sparkles" },
		{ id: 3, color: "from-amber-500 to-red-500", icon: "target" },
	];

	const categoryIcons = ["palette", "laptop", "mobile", "pencil"];
	const mediaIcons = ["fileText", "image", "play", "music"];

	const nextSlide = (carId, total) => {
		setActiveSlide(prev => ({
			...prev,
			[carId]: (prev[carId] + 1) % total
		}));
	};

	const prevSlide = (carId, total) => {
		setActiveSlide(prev => ({
			...prev,
			[carId]: (prev[carId] - 1 + total) % total
		}));
	};

	return (
		<div className="flex flex-col gap-y-10 p-6">
			{/* SECTION 1: BASIC CAROUSELS */}
			<div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
				<h2 className="text-xl font-bold text-white mb-6">Basic Carousels</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">

					{/* Variation 1: Simple Scroll */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Horizontal Scroll</label>
						<div className="w-full overflow-x-auto rounded-lg">
							<div className="flex gap-3 pb-2 min-w-max px-2">
								{[1,2,3,4,5].map(i => (
									<div key={i} className="w-32 h-24 bg-gradient-to-br from-amber-500 to-blue-500 rounded-lg flex items-center justify-center text-2xl flex-shrink-0">
										{i}
									</div>
								))}
							</div>
						</div>
						<CopyCode number_code={1} code={`<div className="w-full overflow-x-auto rounded-lg">
  <div className="flex gap-3 pb-2 min-w-max px-2">
    {slides.map(slide => (
      <div key={slide.id} className="w-32 h-24 bg-gradient rounded-lg flex-shrink-0">
        {slide.content}
      </div>
    ))}
  </div>
</div>`} />
					</div>

					{/* Variation 2: Card Carousel */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Card Carousel</label>
						<div className="space-y-3">
							<div className="flex gap-3 overflow-x-auto pb-2">
								{['Product A', 'Product B', 'Product C'].map((prod, i) => (
										<div key={i} className="flex-shrink-0 w-40 bg-slate-700 border border-slate-600 rounded-lg p-3 hover:border-amber-400/50 transition-all">
										<div className="w-full h-20 bg-gradient-to-br from-amber-500 to-amber-500 rounded mb-2"></div>
										<p className="text-slate-300 text-sm font-semibold">{prod}</p>
										<p className="text-slate-400 text-xs mt-1">$99.99</p>
									</div>
								))}
							</div>
						</div>
						<CopyCode number_code={2} code={`<div className="flex gap-3 overflow-x-auto pb-2">
  {products.map(prod => (
    <div key={prod.id} className="flex-shrink-0 w-40 bg-slate-700 rounded-lg p-3">
      <div className="w-full h-20 bg-gradient rounded mb-2"></div>
      <p className="text-slate-300 text-sm">{prod.name}</p>
    </div>
  ))}
</div>`} />
					</div>

					{/* Variation 3: Button Controls */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">With Controls</label>
						<div className="space-y-3">
							<div className="bg-gradient-to-r from-amber-500 to-blue-500 rounded-lg h-32 flex items-center justify-center text-3xl">
								<Icon name={slides[activeSlide.carousel1]?.icon} className="w-8 h-8" />
							</div>
							<div className="flex gap-2">
								<button 
									onClick={() => prevSlide('carousel1', 3)}
									className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-sm"
								>
									<span className="inline-flex items-center gap-1"><Icon name="arrowLeft" className="w-4 h-4" /> Prev</span>
								</button>
								<button 
									onClick={() => nextSlide('carousel1', 3)}
												className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm"
								>
									<span className="inline-flex items-center gap-1">Next <Icon name="arrowRight" className="w-4 h-4" /></span>
								</button>
							</div>
						</div>
						<CopyCode number_code={3} code={`<div className="bg-gradient-to-r from-amber-500 to-blue-500 rounded-lg h-32 flex items-center justify-center">
	{slides[currentSlide].icon}
</div>
<div className="flex gap-2">
	<button onClick={() => prevSlide()}>Prev</button>
	<button onClick={() => nextSlide()}>Next</button>
</div>`} />
					</div>

					{/* Variation 4: Image Carousel */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Image Gallery</label>
						<div className="space-y-3">
							<div className="border border-slate-600 rounded-lg overflow-hidden flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800 h-32">
								<Icon name="image" className="w-10 h-10" />
							</div>
							<div className="flex gap-2">
								{[1,2,3,4].map(i => (
											<div key={i} className="w-12 h-12 bg-gradient-to-br from-slate-700 to-slate-800 border border-slate-600 rounded hover:border-amber-300 cursor-pointer transition-all">
										{i}
									</div>
								))}
							</div>
						</div>
						<CopyCode number_code={4} code={`<div className="border border-slate-600 rounded-lg flex items-center justify-center bg-gradient h-32">
  <!-- Large image -->
</div>
<div className="flex gap-2">
  {thumbnails.map(thumb => (
    <div key={thumb.id} className="w-12 h-12 rounded hover:border-amber-300">
      {/* Thumbnail */}
    </div>
  ))}
</div>`} />
					</div>

					{/* Variation 5: Auto-play (simulated) */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Auto-Play Style</label>
						<div className="space-y-3">
							<div className="relative w-full h-32 bg-gradient-to-r from-amber-500 to-amber-500 rounded-lg overflow-hidden group">
								<div className="absolute inset-0 flex items-center justify-center text-3xl">
									<Icon name={slides[activeSlide.carousel2]?.icon} className="w-8 h-8" />
								</div>
								<div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
									{slides.map((_, i) => (
										<div key={i} className={`w-2 h-2 rounded-full transition-all ${i === activeSlide.carousel2 ? 'bg-white w-4' : 'bg-white/50'}`}></div>
									))}
								</div>
							</div>
							<p className="text-slate-400 text-xs text-center">Indicator dots below</p>
						</div>
						<CopyCode number_code={5} code={`<div className="relative w-full h-32 bg-gradient rounded-lg">
  <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
    {slides.map((_, i) => (
      <div key={i} className={i === current ? 'bg-white w-4' : 'bg-white/50 w-2'}>
      </div>
    ))}
  </div>
</div>`} />
					</div>

					{/* Variation 6: Cards with Overflow */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Cards Grid</label>
						<div className="overflow-x-auto pb-2">
							<div className="flex gap-3 min-w-max">
								{['Design', 'Development', 'Marketing', 'Content'].map((cat, i) => (
										<div key={i} className="flex-shrink-0 w-28 bg-slate-700 border border-slate-600 rounded-lg p-3 text-center hover:border-orange-400/50 transition-all">
										<div className="text-2xl mb-2"><Icon name={categoryIcons[i]} className="w-6 h-6" /></div>
										<p className="text-slate-300 text-xs font-semibold">{cat}</p>
									</div>
								))}
							</div>
						</div>
						<CopyCode number_code={6} code={`<div className="overflow-x-auto pb-2">
  <div className="flex gap-3 min-w-max">
    {categories.map(cat => (
      <div key={cat.id} className="flex-shrink-0 w-28 bg-slate-700 rounded-lg p-3">
        <div className="text-2xl mb-2">{cat.icon}</div>
        <p className="text-slate-300 text-xs">{cat.name}</p>
      </div>
    ))}
  </div>
</div>`} />
					</div>

				</div>
			</div>

			{/* SECTION 2: ADVANCED CAROUSELS */}
			<div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
				<h2 className="text-xl font-bold text-amber-400 mb-6">Advanced Features</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">

					{/* Variation 7: Pagination */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">With Pagination</label>
						<div className="space-y-3">
							<div className="bg-gradient-to-r from-amber-600 to-blue-600 rounded-lg h-32 flex items-center justify-center text-3xl">
								Item {activeSlide.carousel3 + 1}
							</div>
							<div className="flex items-center justify-between">
								<button 
									onClick={() => prevSlide('carousel3', 5)}
									className="px-2 py-1 text-sm bg-slate-700 hover:bg-slate-600 rounded"
								>
									<Icon name="arrowLeft" className="w-4 h-4" />
								</button>
								<span className="text-xs text-slate-400">{activeSlide.carousel3 + 1} / 5</span>
								<button 
									onClick={() => nextSlide('carousel3', 5)}
												className="px-2 py-1 text-sm bg-amber-600 hover:bg-amber-700 rounded text-white"
								>
									<Icon name="arrowRight" className="w-4 h-4" />
								</button>
							</div>
						</div>
						<CopyCode number_code={7} code={`<div className="flex items-center justify-between">
	<button onClick={() => prevSlide()}>Prev</button>
  <span className="text-xs text-slate-400">
    {current + 1} / {total}
  </span>
	<button onClick={() => nextSlide()}>Next</button>
</div>`} />
					</div>

					{/* Variation 8: Vertical Stack */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Vertical List</label>
						<div className="overflow-y-auto h-40 space-y-2 pr-2">
							{['Item 1', 'Item 2', 'Item 3', 'Item 4', 'Item 5', 'Item 6'].map((item, i) => (
										<div key={i} className="p-3 bg-slate-700 border border-slate-600 rounded-lg hover:border-orange-400/50 transition-all cursor-pointer text-slate-300 text-sm">
									{item}
								</div>
							))}
						</div>
						<CopyCode number_code={8} code={`<div className="overflow-y-auto h-40 space-y-2">
  {items.map((item, i) => (
    <div key={i} className="p-3 bg-slate-700 border border-slate-600 rounded-lg">
      {item}
    </div>
  ))}
</div>`} />
					</div>

					{/* Variation 9: Mixed Content */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Mixed Content</label>
						<div className="overflow-x-auto pb-2">
							<div className="flex gap-3 min-w-max">
								{[
									{ type: 'text', icon: 'fileText' },
									{ type: 'image', icon: 'image' },
									{ type: 'video', icon: 'play' },
									{ type: 'audio', icon: 'music' },
								].map((item, i) => (
									<div key={i} className="flex-shrink-0 w-20 h-20 bg-slate-700 border border-slate-600 rounded-lg flex items-center justify-center text-2xl hover:shadow-lg hover:shadow-amber-500/20 transition-all">
										<Icon name={item.icon} className="w-6 h-6" />
									</div>
								))}
							</div>
						</div>
						<CopyCode number_code={9} code={`<div className="overflow-x-auto pb-2">
  <div className="flex gap-3 min-w-max">
    {items.map((item, i) => (
      <div key={i} className="flex-shrink-0 w-20 h-20 bg-slate-700 rounded-lg flex items-center justify-center">
        {item.content}
      </div>
    ))}
  </div>
</div>`} />
					</div>

					{/* Variation 10: Side Navigation */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Side Navigation</label>
						<div className="flex gap-3 h-32">
							<div className="flex flex-col gap-2 w-16">
								{mediaIcons.map((icon, i) => (
										<button key={i} className={`flex-1 rounded text-xl transition-all ${i === activeSlide.carousel4 ? 'bg-amber-600' : 'bg-slate-700 hover:bg-slate-600'}`}>
										<Icon name={icon} className="w-6 h-6" />
									</button>
								))}
							</div>
							<div className="flex-1 bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg flex items-center justify-center text-3xl">
								<Icon name={mediaIcons[activeSlide.carousel4]} className="w-8 h-8" />
							</div>
						</div>
						<CopyCode number_code={10} code={`<div className="flex gap-3 h-32">
  <div className="flex flex-col gap-2 w-16">
    {nav.map((item, i) => (
      <button key={i} className={current === i ? 'bg-amber-600' : 'bg-slate-700'}>
        {item}
      </button>
    ))}
  </div>
  <div className="flex-1 bg-slate-700 rounded-lg">
    {/* Content */}
  </div>
</div>`} />
					</div>

					{/* Variation 11: Infinite Scroll */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Infinite Scroll</label>
						<div className="overflow-x-auto pb-2">
							<div className="flex gap-3 min-w-max">
								{Array(8).fill(0).map((_, i) => (
										<div key={i} className="flex-shrink-0 w-24 h-24 bg-gradient-to-br from-slate-700 to-slate-600 border border-slate-500 rounded-lg flex items-center justify-center text-sm font-semibold text-slate-300 hover:border-amber-400/50 transition-all">
										Item {(i % 4) + 1}
									</div>
								))}
							</div>
						</div>
						<CopyCode number_code={11} code={`<div className="overflow-x-auto pb-2">
  <div className="flex gap-3 min-w-max">
    {Array(infinite).fill(0).map((_, i) => (
      <div key={i} className="flex-shrink-0 w-24 h-24 rounded-lg">
        Item {i}
      </div>
    ))}
  </div>
</div>`} />
					</div>

					{/* Variation 12: Testimonials */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Testimonials</label>
						<div className="space-y-3">
							<div className="bg-gradient-to-r from-amber-900/40 to-amber-900/40 border border-amber-500/30 rounded-lg p-4">
								<p className="text-slate-300 text-sm italic mb-2">"{['Great service!', 'Highly recommend', 'Perfect solution'][activeSlide.carousel5 % 3]}"</p>
								<div className="flex items-center gap-2">
									<div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center text-xs"><Icon name="users" className="w-4 h-4" /></div>
									<div>
										<p className="text-slate-100 text-xs font-semibold">User {activeSlide.carousel5 + 1}</p>
										<p className="text-slate-400 text-xs inline-flex items-center gap-1"><Icon name="star" className="w-3 h-3" /><Icon name="star" className="w-3 h-3" /><Icon name="star" className="w-3 h-3" /><Icon name="star" className="w-3 h-3" /><Icon name="star" className="w-3 h-3" /></p>
									</div>
								</div>
							</div>
							<div className="flex gap-1 justify-center">
								{[0,1,2].map(i => (
									<button key={i} onClick={() => setActiveSlide(prev => ({...prev, carousel5: i}))} className={`w-2 h-2 rounded-full transition-all ${i === activeSlide.carousel5 ? 'bg-amber-400 w-4' : 'bg-slate-500'}`}></button>
								))}
							</div>
						</div>
						<CopyCode number_code={12} code={`<div className="bg-gradient-to-r from-amber-900/40 to-amber-900/40 border border-amber-500/30 rounded-lg p-4">
  <p className="text-slate-300 text-sm italic">
    "{testimonials[current].text}"
  </p>
  <div className="flex items-center gap-2 mt-2">
	<p className="text-xs">Rating: 5/5</p>
  </div>
</div>`} />
					</div>

				</div>
			</div>
		</div>
	);
};

export default CarouselComponent;



